import { Link, useParams } from 'react-router-dom';
import { api } from '../../lib/api';
import { useResource } from '../../lib/useResource';
import { Pill, StateCard } from '../../components/StateCard';
import { GameArt, PLATFORM_LABEL, formatDate, lifecycleLabel, lifecycleTone, relativeDate, Section } from '../../components/site';
import type { ApiEnvelope } from '../../lib/types';

interface Destination {
  platform: string;
  label: string;
  target_id: string | null;
  playable: boolean;
  reason: string | null;
}

interface GameDetailPayload {
  id: string; slug: string; name: string; tagline: string | null; genre: string | null;
  platforms: string[]; lifecycle: string; about: string | null; features: string[];
  age_rating: string | null; locales: string[]; regions: string[];
  released_at: string | null; destinations: Destination[]; support_path: string;
}

interface NewsItem {
  id: string; slug: string; kind: string; title: string; excerpt: string; published_at: string | null;
}

const REASON_COPY: Record<string, string> = {
  coming_soon: '아직 출시 전.',
  destination_not_registered: '이 플랫폼 경로가 아직 등록 전.',
  destination_not_verified: '이동 주소 확인 필요',
  destination_blocked: '정책상 차단된 경로.',
  destination_expired: '링크 만료.',
};

/**
 * P003 — game detail.
 *
 * A PLAY button exists only where a verified destination exists. Where it does
 * not, the reason is shown instead of a dead button.
 */
export function PublicGameDetail() {
  const { slug } = useParams();

  const game = useResource<GameDetailPayload>(
    (signal) => api.public(`/api/v1/games/${slug}`, { signal }) as Promise<ApiEnvelope<GameDetailPayload>>,
    [slug],
    { emptyWhen: () => false },
  );

  const news = useResource<NewsItem[]>(
    (signal) => api.public('/api/v1/news', { signal, query: { game: slug, per_page: 4 } }) as Promise<ApiEnvelope<NewsItem[]>>,
    [slug],
  );

  const data = game.data;
  const playable = data?.destinations.filter((destination) => destination.playable) ?? [];
  const unavailable = data?.destinations.filter((destination) => !destination.playable) ?? [];

  return (
    <StateCard state={game.state} error={game.error} onRetry={game.reload}>
      {data && (
        <>
          <nav className="breadcrumb" style={{ marginBottom: 20 }}>
            <Link to="/games">전체 게임</Link> / {data.name}
          </nav>

          <section className="game-hero">
            <GameArt game={data} className="game-hero__art" />

            <div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                <Pill tone={lifecycleTone(data.lifecycle)}>{lifecycleLabel(data.lifecycle)}</Pill>
                {data.genre && <Pill tone="neutral">{data.genre}</Pill>}
                {data.age_rating && <Pill tone="neutral">{data.age_rating}</Pill>}
              </div>

              <h1 className="game-hero__title">{data.name}</h1>
              {data.tagline && <p className="game-hero__tagline">{data.tagline}</p>}

              <div className="play-row">
                {playable.map((destination) => (
                  <Link key={destination.platform} className="btn btn--primary" to={`/go/${destination.target_id}`}>
                    {destination.label}에서 열기
                  </Link>
                ))}

                {playable.length === 0 && unavailable.length > 0 && (
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    {unavailable.map((destination) => (
                      <div key={destination.platform} className="play-option">
                        <button type="button" className="btn" disabled>
                          {PLATFORM_LABEL[destination.platform] ?? destination.platform}
                        </button>
                        <span className="play-option__reason">
                          {REASON_COPY[destination.reason ?? ''] ?? '지금은 이용할 수 없어요.'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {data.destinations.length === 0 && (
                  <p style={{ color: 'var(--text-tertiary)', fontSize: 14, margin: 0 }}>
                    등록된 플랫폼이 없어요.
                  </p>
                )}
              </div>

              {playable.length > 0 && unavailable.length > 0 && (
                <p style={{ fontSize: 13, color: 'var(--text-tertiary)', margin: '4px 0 0' }}>
                  아직 열리지 않은 플랫폼: {unavailable.map((d) => PLATFORM_LABEL[d.platform] ?? d.platform).join(', ')}
                </p>
              )}
            </div>
          </section>

          <div className="grid grid--2" style={{ alignItems: 'start' }}>
            <div>
              {data.about && (
                <Section title="소개">
                  <p style={{ whiteSpace: 'pre-wrap', lineHeight: 1.8, margin: 0 }}>{data.about}</p>
                </Section>
              )}

              {data.features.length > 0 && (
                <Section title="이런 게임">
                  <ul className="feature-list">
                    {data.features.map((feature) => <li key={feature}>{feature}</li>)}
                  </ul>
                </Section>
              )}
            </div>

            <div>
              <Section title="정보">
                <dl className="spec-list">
                  <dt>플랫폼</dt>
                  <dd>{data.platforms.map((platform) => PLATFORM_LABEL[platform] ?? platform).join(', ')}</dd>
                  <dt>출시</dt>
                  <dd>{data.released_at ? formatDate(data.released_at) : '미정'}</dd>
                  <dt>연령 안내</dt>
                  <dd>{data.age_rating ?? '—'}</dd>
                  <dt>지원 언어</dt>
                  <dd>{data.locales.length ? data.locales.join(', ') : '—'}</dd>
                  <dt>서비스 지역</dt>
                  <dd>{data.regions.length ? data.regions.join(', ') : '—'}</dd>
                </dl>

                <div style={{ marginTop: 20, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <Link className="btn" to={data.support_path}>이 게임 문의하기</Link>
                  <Link className="btn" to={`/news?game=${data.slug}`}>소식 전체 보기</Link>
                </div>
              </Section>
            </div>
          </div>

          <Section title="이 게임의 소식" moreHref={`/news?game=${data.slug}`}>
            {(news.data?.length ?? 0) === 0 ? (
              <p style={{ color: 'var(--text-tertiary)', fontSize: 14 }}>아직 등록된 소식이 없어요.</p>
            ) : (
              <div className="row-list">
                {news.data!.map((item) => (
                  <Link key={item.id} to={`/news/${item.slug}`} className="row">
                    <div className="row__main">
                      <h3 className="row__title">{item.title}</h3>
                      <p className="row__excerpt">{item.excerpt}</p>
                    </div>
                    <div className="row__aside">
                      <Pill tone={item.kind === 'patch_note' ? 'info' : 'neutral'}>
                        {item.kind === 'patch_note' ? '패치노트' : '공지'}
                      </Pill>
                      <span>{relativeDate(item.published_at)}</span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </Section>
        </>
      )}
    </StateCard>
  );
}
